/**
 * Auth signal + helpers used across the UI.
 * Keep it tiny — a single signal + derived accessors.
 */

import { createSignal, createMemo } from "solid-js";
import { loadFromStorage, refresh } from "./oidc";
import type { AuthState, PacsAction } from "./types";
import { can as canFn } from "./types";

const [auth, setAuth] = createSignal<AuthState | null>(loadFromStorage());

// Schedule a silent refresh when token is near expiry.
let refreshTimer: ReturnType<typeof setTimeout> | null = null;
function scheduleRefresh(s: AuthState | null) {
  if (refreshTimer) { clearTimeout(refreshTimer); refreshTimer = null; }
  if (!s?.refreshToken) return;
  const msUntilExpiry = s.expiresAt * 1000 - Date.now();
  const wait = Math.max(15_000, msUntilExpiry - 60_000);
  refreshTimer = setTimeout(async () => {
    const next = await refresh(s);
    if (next) setAuth(next);
    else setAuth(null);
  }, wait);
}
scheduleRefresh(auth());

export function setAuthState(s: AuthState | null) {
  setAuth(s);
  scheduleRefresh(s);
}

export { auth };

export function isLoggedIn(): boolean { return !!auth(); }

export function can(action: PacsAction): boolean {
  return canFn(auth(), action);
}

export const canAdmin            = createMemo(() => can("ui.admin.login"));
export const canUpload           = createMemo(() => can("studies.update_meta"));
export const canMeasure          = createMemo(() => can("studies.read"));
export const canSignReport       = createMemo(() => can("reports.sign"));
export const canAudit            = createMemo(() => can("audit.read"));
export const canAcl              = createMemo(() => can("rbac.update"));

/** Display-name fallback chain. */
export function displayName(): string {
  const s = auth();
  if (!s) return "Guest";
  return s.claims.name
      ?? s.claims.preferred_username
      ?? s.claims.email
      ?? s.claims.sub;
}

/** Patch the in-memory claims so the user-name chip + every
 *  other consumer of auth().claims see the new values immediately
 *  after a self-service profile save.  The JWT itself stays the
 *  same — the next refresh / sign-in picks the canonical values up
 *  from the identity provider.  Persists to sessionStorage so a
 *  full reload doesn't snap back to the stale claim. */
export function patchClaims(patch: {
  name?: string; preferred_username?: string; email?: string;
}) {
  const s = auth();
  if (!s) return;
  const next: AuthState = { ...s, claims: { ...s.claims, ...patch } };
  setAuth(next);
  try { sessionStorage.setItem("vpd.auth.v1", JSON.stringify(next)); } catch { /* fine */ }
}
